/**
 * Hooks for SOP Proposals Inbox (Phase 23.2.1 — Living SOPs)
 *
 * TanStack Query hooks for the trainer review queue at /sops/proposals.
 * Polls every 60s (blueprint §Chunk 23.2.1 refetchInterval).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  ProposalEvidence,
  SopProposalChanges,
  SopDraftProposalState,
  SopDraftProposalSource,
  ProposalImpactBucket,
  ProposalAgeBucket,
  ProposalInboxSort,
} from '@guild/shared';

// ── Response shapes ─────────────────────────────────────────────────────────
// Must stay in sync with the handler response in apps/api/src/routes/sops.ts.

export interface ProposalBreadcrumb {
  sopTitle: string | null;
  sectionTitle: string | null;
  taskTitle: string | null;
}

export interface SopProposalListItem {
  id: string;
  sopId: string;
  sopTitle: string | null;
  state: SopDraftProposalState;
  source: SopDraftProposalSource;
  impactScore: number;
  confidence: number;
  safetyCheckPassed: boolean;
  conflictDetected: boolean;
  changes: SopProposalChanges;
  evidence: ProposalEvidence;
  rationale: string;
  breadcrumb: ProposalBreadcrumb;
  createdAt: string;
  authoredByUserId: string | null;
  reviewerUserId: string | null;
}

export interface SopProposalsResponse {
  data: SopProposalListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  meta: {
    atCapacity: boolean;
    pendingCount: number;
    maxPending: number;
  };
}

export interface SopProposalsQueryParams {
  state?: SopDraftProposalState;
  source?: SopDraftProposalSource;
  sopId?: string;
  impactBucket?: ProposalImpactBucket;
  ageBucket?: ProposalAgeBucket;
  sort?: ProposalInboxSort;
  page?: number;
  limit?: number;
}

// ── Query key factory ───────────────────────────────────────────────────────

export interface ProposalDetailQueueContext {
  state?: SopDraftProposalState;
  source?: SopDraftProposalSource;
  sort?: ProposalInboxSort;
}

export const sopProposalKeys = {
  all: ['sopProposals'] as const,
  lists: () => [...sopProposalKeys.all, 'list'] as const,
  list: (params: SopProposalsQueryParams) =>
    [...sopProposalKeys.lists(), params] as const,
  counts: () => [...sopProposalKeys.all, 'count'] as const,
  details: () => [...sopProposalKeys.all, 'detail'] as const,
  detail: (id: string, ctx?: ProposalDetailQueueContext) =>
    [...sopProposalKeys.details(), id, ctx ?? {}] as const,
};

// ── Hooks ───────────────────────────────────────────────────────────────────

function buildQueryString(params: SopProposalsQueryParams): string {
  const sp = new URLSearchParams();
  if (params.state) sp.set('state', params.state);
  if (params.source) sp.set('source', params.source);
  if (params.sopId) sp.set('sopId', params.sopId);
  if (params.impactBucket) sp.set('impactBucket', params.impactBucket);
  if (params.ageBucket) sp.set('ageBucket', params.ageBucket);
  if (params.sort) sp.set('sort', params.sort);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch paginated SOP proposals (inbox list).
 * Polls every 60s so a trainer leaving the tab open sees new proposals land.
 */
export function useSopProposals(params: SopProposalsQueryParams = {}) {
  const api = useApi();
  return useQuery({
    queryKey: sopProposalKeys.list(params),
    queryFn: () =>
      api.get<SopProposalsResponse>(
        `/api/sops/proposals${buildQueryString(params)}`
      ),
    refetchInterval: 60_000, // blueprint §Chunk 23.2.1
  });
}

/**
 * Count of pending_review proposals — for the nav badge.
 *
 * Gated on roleLevel >= 3 (manager) to match the server-side role requirement.
 * Calling the endpoint with a lower role would 403, so we skip the query.
 */
export function useSopProposalsPendingCount(roleLevel: number = 0) {
  const api = useApi();
  return useQuery({
    queryKey: sopProposalKeys.counts(),
    queryFn: async () => {
      const res = await api.get<SopProposalsResponse>(
        '/api/sops/proposals?state=pending_review&page=1&limit=1'
      );
      // Prefer the org-wide pending count from meta (independent of filters).
      return res.meta?.pendingCount ?? res.pagination?.total ?? 0;
    },
    enabled: roleLevel >= 3,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ── Detail (Phase 23.2.2) ────────────────────────────────────────────────────

/** Per-step snapshot returned by the detail endpoint (target + proposed). */
export interface SnapshotStep {
  id: string;
  stepNumber: number;
  description: string;
  expectedDuration: number | null;
  isSafetyCritical: boolean;
  imageS3Key: string | null;
  keywords: string[] | null;
  _changeType?: 'unchanged' | 'updated' | 'inserted' | 'deleted' | 'reordered';
  _originalStepId?: string;
  _placeholderId?: string;
  _originalOrderIndex?: number;
}

export interface SnapshotTask {
  id: string;
  taskNumber: number;
  title: string;
  description: string | null;
  estimatedDuration: number | null;
  isSafetyCritical: boolean;
  steps: SnapshotStep[];
  _changeType?: 'unchanged' | 'updated' | 'inserted' | 'deleted';
}

export interface SnapshotSection {
  id: string;
  sectionNumber: number;
  title: string;
  description: string | null;
  whenToDoTrigger: string | null;
  tasks: SnapshotTask[];
  _changeType?: 'unchanged' | 'updated' | 'inserted' | 'deleted';
}

export interface VersionSnapshot {
  sections: SnapshotSection[];
}

export type SafetyFlagKind =
  | 'delete_safety_critical'
  | 'update_safety_critical_text'
  | 'update_safety_critical_duration'
  | 'reorder_safety_critical'
  | 'task_delete_safety_critical';

export interface SafetyFlag {
  operationIndex: number;
  operationType: string;
  stepId?: string;
  taskId?: string;
  flagKind: SafetyFlagKind;
  severity: 'block' | 'warn';
  message: string;
}

/**
 * Phase 23.6.1: Gemini 2.5 Pro bounding-box callout. Stored verbatim in
 * `sopStepMedia.annotations` JSONB; the SVG overlay in `RedlineStep.tsx`
 * renders these with `viewBox="0 0 1000 1000"` so the 0-1000 coordinates map
 * directly without transformation.
 *
 * CRITICAL: `box_2d[0]` is Y, `box_2d[1]` is X, `box_2d[2]` is Y_end,
 * `box_2d[3]` is X_end. Do NOT swap — order is pinned per Vertex AI docs.
 */
export interface CalloutBox {
  number: number;
  label: string;
  box_2d: [number, number, number, number]; // [y0, x0, y1, x1] in 0-1000
  confidence?: number;
}

export interface SopStepMediaAnnotations {
  generatedAt: string;
  generator: 'gemini-2.5-pro' | 'gemini-3.1-pro-preview';
  calloutCount: number;
  callouts: CalloutBox[];
}

/**
 * Phase 23.4.3 — Per-evidence-item assessment-level score context. Captured
 * at proposal-creation time and rendered on the redline citation strip
 * (timestamp / score badge / operator name / trigger link). When absent
 * (legacy V1 proposals or struggle-signal items), the strip falls back to
 * the existing "View source assessment" link with a `(legacy)` modifier.
 */
export interface ProposalEvidenceScoreContext {
  complianceScore: number;
  safetyScore: number;
  efficiencyScore: number;
  overallScore: number;
  operatorId: string | null;
}

export interface SignedEvidenceItem {
  id?: string;
  kind: 'frame' | 'video_clip' | 'uploaded_image';
  s3Key: string;
  signedUrl: string | null;
  caption?: string;
  sourceAssessmentId?: string;
  sourceTimestamp?: number;
  durationSeconds?: number;
  // Phase 23.6.1: raw Gemini 2.5 Pro bounding-box callouts. null/undefined
  // when callouts were never generated or the kill switch was off at harvest
  // time.
  annotations?: SopStepMediaAnnotations;
  // Phase 23.4.3 — citation linkage.
  sourceTriggerId?: string;
  scoreContext?: ProposalEvidenceScoreContext;
  /**
   * Resolved at server read time via JOIN to operators (scoped by orgId).
   * Absent when scoreContext is missing, or when the operator could not be
   * resolved in the caller's org (defense in depth — never reveals foreign-
   * org operator names).
   */
  operatorName?: string;
}

export interface RejectionHistoryItem {
  stepId: string;
  source: SopDraftProposalSource;
  reason: string;
  expiresAt: string | null;
  createdAt: string;
  stepDescription: string;
  taskTitle: string | null;
  sectionTitle: string | null;
}

/**
 * Phase 23.2.5: shape of a single entry in `trainerFeedbackHistory`.
 * Appended by POST /revise each cycle. `priorSnapshot` preserves the
 * changes/rationale/confidence that were overwritten by this revise.
 */
export interface TrainerFeedbackHistoryEntry {
  feedback: string;
  revisedAt: string;
  priorSnapshot: {
    changes: SopProposalChanges;
    rationale: string;
    confidence: number;
  };
}

export interface SopProposalDetail {
  id: string;
  sopId: string;
  sopTitle: string | null;
  targetSopVersionId: string;
  targetVersionNumber: string | null;
  state: SopDraftProposalState;
  source: SopDraftProposalSource;
  confidence: number;
  impactScore: number;
  safetyCheckPassed: boolean;
  safetyCheckNotes: string | null;
  conflictDetected: boolean;
  conflictDetails: unknown | null;
  rationale: string;
  changes: SopProposalChanges;
  evidence: ProposalEvidence;
  /**
   * Phase 23.4.3 — proposal-level citation linkage. Null for manual
   * proposals (no triggering assessment) and for legacy V1 proposals that
   * pre-date the schema population.
   */
  sourceTriggerId: string | null;
  sourceAssessmentId: string | null;
  reviseCount: number;
  // Phase 23.2.5: null when the proposal has never been revised.
  trainerFeedbackHistory: TrainerFeedbackHistoryEntry[] | null;
  authoredByUserId: string | null;
  authoredByUserName: string | null;
  reviewerUserId: string | null;
  reviewerUserName: string | null;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  publishedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  reviewNote: string | null;
}

export interface SopProposalDetailResponse {
  proposal: SopProposalDetail;
  targetVersionSnapshot: VersionSnapshot;
  proposedSnapshot: VersionSnapshot;
  evidence: SignedEvidenceItem[];
  affectedOperatorCount: number;
  rejectionHistory: RejectionHistoryItem[];
  safetyFlags: SafetyFlag[];
  nextProposalId: string | null;
  prevProposalId: string | null;
  queuePosition: { index: number; total: number } | null;
}

function buildDetailQueryString(ctx: ProposalDetailQueueContext = {}): string {
  const sp = new URLSearchParams();
  if (ctx.state) sp.set('state', ctx.state);
  if (ctx.source) sp.set('source', ctx.source);
  if (ctx.sort) sp.set('sort', ctx.sort);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch a single proposal with target/proposed snapshots + queue nav context.
 *
 * `queueContext` lets the detail page preserve the inbox's sort/filter so the
 * j/k keyboard shortcuts navigate the same queue the user was browsing.
 */
export function useSopProposal(
  proposalId: string | undefined,
  queueContext: ProposalDetailQueueContext = {}
) {
  const api = useApi();
  return useQuery({
    queryKey: sopProposalKeys.detail(proposalId ?? '', queueContext),
    queryFn: () =>
      api.get<SopProposalDetailResponse>(
        `/api/sops/proposals/${proposalId}${buildDetailQueryString(queueContext)}`
      ),
    enabled: !!proposalId,
    // No polling — the page is active-user-driven. Explicit refetch on
    // approve/reject/revise actions (Chunks 23.2.3+).
    staleTime: 10_000,
  });
}

/**
 * Derive queue navigation + position from the detail query result.
 * Returns null when the proposal isn't in the queue (e.g., already reviewed
 * but viewed via direct link).
 */
export function useProposalQueueNav(
  detail: SopProposalDetailResponse | undefined
) {
  if (!detail) return null;
  return {
    nextId: detail.nextProposalId,
    prevId: detail.prevProposalId,
    position: detail.queuePosition,
  };
}

// ── Approve + cascade status (Phase 23.2.3) ──────────────────────────────────

/**
 * Response from POST /api/sops/proposals/:id/approve.
 *
 * publishNewVersion returns as soon as Phase A commits (<500ms typical). Phase
 * B runs fire-and-forget in the background. The trainer sees cascade progress
 * via the polling status endpoint below.
 */
export interface ApproveProposalResult {
  versionId: string;
  versionSequence: number;
}

/**
 * Cascade status payload — normalized shape returned by
 * GET /api/sops/proposals/:id/cascade-status.
 *
 * stage:
 *  - 'idle'       — proposal not yet approved; overlay should close
 *  - 'cascading'  — Phase B in progress; show progress bar
 *  - 'complete'   — cascade finished; transition to success state
 */
export type CascadeStatus =
  | {
      stage: 'idle';
      step: 0;
      total: number;
      label: string;
    }
  | {
      stage: 'cascading';
      versionId: string | null;
      step: number;
      total: number;
      label: string;
      startedAt: string | null;
      elapsedMs: number;
    }
  | {
      stage: 'complete';
      versionId: string;
      versionNumber: string;
      changeSummary: string | null;
      step: number;
      total: number;
      label: string;
    };

/**
 * Mutation: approve a pending_review proposal.
 *
 * Resolves to { versionId, versionSequence } once Phase A of the cascade
 * commits on the server. Callers should then flip the cascade overlay to
 * visible and start polling via useCascadeStatus.
 *
 * Invalidates the inbox list on success so the approved proposal drops out
 * of pending_review views on the next fetch.
 */
export function useApproveProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    ApproveProposalResult,
    Error,
    { proposalId: string; stepApprovalsViewed: true }
  >({
    mutationFn: ({ proposalId, stepApprovalsViewed }) =>
      api.post<ApproveProposalResult>(
        `/api/sops/proposals/${proposalId}/approve`,
        { stepApprovalsViewed }
      ),
    onSuccess: (_result, { proposalId }) => {
      // Inbox list + pending count + detail view are all stale after approve.
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.counts() });
      queryClient.invalidateQueries({
        queryKey: sopProposalKeys.detail(proposalId),
      });
    },
  });
}

// ── Reject (Phase 23.2.4) ────────────────────────────────────────────────────

/**
 * Rejection reason enum — must match the backend REJECTION_REASONS const in
 * apps/api/src/routes/sops.ts and LIVING_SOPS_CONFIG.SUPPRESSION_TTL_DAYS
 * keys. Server rejects unknown values with 400 VALIDATION_ERROR.
 */
export type RejectProposalReason =
  | 'already_correct'
  | 'missing_context'
  | 'safety'
  | 'cosmetic'
  | 'other';

export interface RejectProposalInput {
  proposalId: string;
  reason: RejectProposalReason;
  note?: string;
}

export interface RejectProposalResult {
  proposalId: string;
  state: 'rejected';
  rejectionReason: RejectProposalReason;
  rejectedAt: string;
}

/**
 * Mutation: reject a pending_review proposal.
 *
 * On success the server:
 *   - marks the proposal state='rejected' with rejectedAt/rejectionReason/reviewNote
 *   - UPSERTs a sop_proposal_suppressions row per touched stepId with a
 *     reason-specific TTL (safety = permanent)
 *   - INSERTs a sop_proposal_rejected notification for the manual author
 *     (when source='manual' and the author isn't the caller)
 *
 * Invalidates the inbox list, pending count, and the detail view so the
 * rejected proposal drops out of default pending views on the next fetch.
 * Errors surface to the caller (400 validation / 403 four-eyes / 404 /
 * 409 already-reviewed) — the modal renders the message in a banner.
 */
export function useRejectProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<RejectProposalResult, Error, RejectProposalInput>({
    mutationFn: ({ proposalId, reason, note }) =>
      api.post<RejectProposalResult>(
        `/api/sops/proposals/${proposalId}/reject`,
        { reason, note }
      ),
    onSuccess: (_result, { proposalId }) => {
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.counts() });
      queryClient.invalidateQueries({
        queryKey: sopProposalKeys.detail(proposalId),
      });
    },
  });
}

// ── Revise with feedback (Phase 23.2.5) ─────────────────────────────────────

/**
 * Input for POST /api/sops/proposals/:id/revise.
 *
 * `feedback` — natural-language description of what the AI should change.
 * Min 10 chars (backend enforces); max 2000 (UI truncates at the textarea).
 *
 * `targetStepIds` — optional hint telling the AI which steps the trainer
 * wants to focus on. Surfaced in the Claude prompt; Claude may touch other
 * steps if the feedback logically implies it.
 */
export interface ReviseProposalInput {
  proposalId: string;
  feedback: string;
  targetStepIds?: string[];
}

/**
 * Response shape from the revise endpoint.
 *
 * `unchanged: true` signals Claude produced bytes-identical output (the
 * trainer's feedback was too vague). UI shows "AI didn't change anything"
 * and keeps the feedback panel open so the trainer can refine.
 *
 * `unchanged: false` means the proposal row was updated; the UI should
 * close the feedback panel and refetch the detail query to re-render with
 * the new content.
 */
export type ReviseProposalResult =
  | {
      proposalId: string;
      unchanged: true;
      message: string;
      reviseCount: number;
      changes: SopProposalChanges;
      rationale: string;
      confidence: number;
      safetyCheckPassed: boolean;
    }
  | {
      proposalId: string;
      unchanged: false;
      reviseCount: number;
      changes: SopProposalChanges;
      rationale: string;
      confidence: number;
      safetyCheckPassed: boolean;
      safetyCheckNotes: string | null;
    };

/**
 * Mutation: ask the AI to revise a pending_review proposal using trainer
 * natural-language feedback.
 *
 * Phase 23.2.5 design lock (§15 Q7): this is the ONLY way to modify proposal
 * content from pending_review — trainers cannot directly edit the text. The
 * proposal remains 100% AI-authored after revise, so four-eyes is NOT
 * triggered by this path (enforced server-side).
 *
 * On success:
 *   - `unchanged: false` → invalidate the detail query so the proposal
 *     re-renders with the revised content. Invalidate lists because
 *     reviseCount / impact may have changed.
 *   - `unchanged: true` → do NOT close the feedback panel; the caller
 *     inspects the response and keeps the textarea open for refinement.
 *
 * Errors (surfaced to caller via onError):
 *   - 400 validation — feedback too short or too long
 *   - 403 — caller is not trainer+
 *   - 404 — proposal not found in caller's org
 *   - 409 — proposal already reviewed (approved/rejected)
 *   - 502 — Claude API error or malformed output. "Try again" is the right
 *           UX response; the prior proposal row is intact.
 */
export function useReviseProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ReviseProposalResult, Error, ReviseProposalInput>({
    mutationFn: ({ proposalId, feedback, targetStepIds }) =>
      api.post<ReviseProposalResult>(
        `/api/sops/proposals/${proposalId}/revise`,
        { feedback, targetStepIds }
      ),
    onSuccess: (result, { proposalId }) => {
      // When Claude produced an identical output, no DB write happened — the
      // detail/list queries are still fresh. Skip invalidation to avoid a
      // useless round-trip.
      if (result.unchanged) return;
      queryClient.invalidateQueries({
        queryKey: sopProposalKeys.detail(proposalId),
      });
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.lists() });
    },
  });
}

// ── Request evidence clip (Phase 23.4.2) ───────────────────────────────────

export interface RequestEvidenceClipInput {
  proposalId: string;
  mediaId: string;
  /**
   * Optional clip length override. Defaults server-side to
   * LIVING_SOPS_CONFIG.EVIDENCE.CLIP_SECONDS (23s). Bounded [10, 60].
   */
  clipSeconds?: number;
}

/**
 * Response shape from POST /api/sops/proposals/:id/request-clip.
 *
 * Backend returns 202 Accepted with { jobId, status: 'queued' } on the
 * happy path. Errors surface via the api client as thrown ApiError (409
 * for not_a_frame / cap_exceeded, 410 for source_expired, 503 for
 * feature_disabled). Callers should render the `message` field in a
 * toast.
 */
export interface RequestEvidenceClipResult {
  jobId: string;
  status: 'queued';
}

/**
 * Mutation: ask the backend to generate a ~23s video clip from an existing
 * frame evidence asset. The backend enqueues a BullMQ job; the worker
 * writes a new `sopStepMedia` row on completion (typically 30-60s later).
 *
 * On success we invalidate the proposal detail query so the next refetch
 * picks up the newly-inserted clip. The detail endpoint re-signs S3 URLs
 * on every GET so the swap is seamless — no local cache manipulation
 * needed.
 *
 * The detail query does not poll on its own (staleTime: 10_000, no
 * refetchInterval). For the initial wire-up we rely on TanStack Query's
 * window-focus + manual refetch to surface the new clip; a follow-up
 * chunk can add an explicit poll once we observe real harvest latency
 * on staging.
 */
export function useRequestEvidenceClip() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    RequestEvidenceClipResult,
    Error,
    RequestEvidenceClipInput
  >({
    mutationFn: ({ proposalId, mediaId, clipSeconds }) =>
      api.post<RequestEvidenceClipResult>(
        `/api/sops/proposals/${proposalId}/request-clip`,
        { mediaId, clipSeconds }
      ),
    onSuccess: (_result, { proposalId }) => {
      // Invalidate all detail variants for this proposal (queueContext ?? {}).
      queryClient.invalidateQueries({
        queryKey: sopProposalKeys.detail(proposalId),
      });
    },
  });
}

/**
 * Polling query for cascade progress. Starts returning data as soon as
 * `enabled` flips true (typically right after approve mutation succeeds).
 *
 * Polling:
 *  - Interval: 500 ms. Matches server-side comment that a single-row JSONB
 *    read is <5 ms, so 2 requests/s on one row is negligible.
 *  - Stops automatically when stage === 'complete' (refetchInterval returns
 *    false). Callers can also set `enabled=false` to stop polling (e.g., on
 *    page unmount or timeout).
 *
 * Error handling: underlying query retries default to once (TanStack default).
 * A 404 means the proposal was deleted out from under the caller — rare, so we
 * surface the error to the overlay which shows a failure state.
 */
export function useCascadeStatus(
  proposalId: string | undefined,
  enabled: boolean
) {
  const api = useApi();
  return useQuery<CascadeStatus>({
    // Distinct sub-key so this cache entry is isolated from the detail query.
    queryKey: [...sopProposalKeys.detail(proposalId ?? ''), 'cascade-status'],
    queryFn: () =>
      api.get<CascadeStatus>(
        `/api/sops/proposals/${proposalId}/cascade-status`
      ),
    enabled: !!proposalId && enabled,
    // The cascade is live state — never serve stale.
    staleTime: 0,
    gcTime: 0,
    // Refetch every 500 ms until complete; stop on 'complete' or 'idle'.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 500;
      if (data.stage === 'complete') return false;
      if (data.stage === 'idle') return false;
      return 500;
    },
    // Keep polling while tab is unfocused — the trainer may alt-tab during a
    // 15s cascade and want to come back to the success toast already fired.
    refetchIntervalInBackground: true,
  });
}
